import type {
  RiskSimulationResponse,
  RiskSimulationTable,
  SimulationPoint,
} from '@/types/generated/public_api'

/**
 * Expand a compact `RiskSimulationTable` into the per-point list the risk slide and
 * hedge chart read. Cells are row-major by timestamp: `t * prices.length + p`.
 */
function tableToPoints(table: RiskSimulationTable): SimulationPoint[] {
  const width = table.prices.length
  const at = (column: number[], i: number) => column[i] ?? 0
  return table.timestamps.flatMap((timestamp, t) =>
    table.prices.map((price, p) => {
      const i = t * width + p
      return {
        price,
        timestamp,
        pnl: at(table.pnl, i),
        delta: at(table.delta, i),
        gamma: at(table.gamma, i),
        vega: at(table.vega, i),
        rho: at(table.rho, i),
        theta: at(table.theta, i),
        vanna: at(table.vanna, i),
        volga: at(table.volga, i),
        weightedVega: at(table.weightedVega, i),
      }
    })
  )
}

/**
 * The point lists are deprecated on the wire; derive them from the tables so every
 * caller reads one shape. A response without tables (older lambda) passes through.
 */
export function withPointLists(response: RiskSimulationResponse): RiskSimulationResponse {
  if (!response.tableUsd || !response.tableBase) return response
  return {
    ...response,
    resultsUsd: tableToPoints(response.tableUsd),
    resultsBase: tableToPoints(response.tableBase),
  }
}

/** PnL attribution terms, in display order. */
export const ATTRIBUTION_KEYS = [
  'attributionDelta',
  'attributionGamma',
  'attributionVega',
  'attributionTheta',
  'attributionVanna',
  'attributionVolga',
  'attributionCoinRevaluation',
  'attributionResidual',
] as const

/** Table columns `SimulationPoint` doesn't carry; read straight from the table. */
type TableColumnKey = 'charm' | 'smileDelta' | 'skewDelta' | (typeof ATTRIBUTION_KEYS)[number]

/** Per-account margin series key: `im_0`, `mm_0`, … indexed like `response.accounts`. */
type MarginKind = 'im' | 'mm'
export const marginKey = (kind: MarginKind, account: number) => `${kind}_${account}`
const MARGIN_KEY = /^(im|mm)_(\d+)$/

function columnOf(table: RiskSimulationTable, key: string): number[] | undefined {
  const margin = MARGIN_KEY.exec(key)
  if (margin) {
    const account = table.accounts[Number(margin[2])]
    return margin[1] === 'im' ? account?.initialMargin : account?.maintenanceMargin
  }
  const column = table[key as TableColumnKey]
  return Array.isArray(column) ? column : undefined
}

/**
 * One cell of a table-only column (`TableColumnKey` or a `marginKey`) at a grid
 * point. Unlike the point lists' greeks, an empty column (term doesn't apply, e.g.
 * coin revaluation in USD) or a NaN cell (no defensible MM) is `undefined` — shown
 * blank, never as 0.
 */
export function tableCellAt(
  table: RiskSimulationTable | undefined,
  key: string,
  timestamp: string,
  price: number
): number | undefined {
  if (!table) return undefined
  const column = columnOf(table, key)
  const t = table.timestamps.indexOf(timestamp)
  const p = table.prices.indexOf(price)
  if (!column?.length || t < 0 || p < 0) return undefined
  const value = column[t * table.prices.length + p]
  return value === undefined || Number.isNaN(value) ? undefined : value
}
